import { ConnectionQualityLevels } from "@minervaproject/avsubsystem-core";
import { partition, shuffle } from "lodash";

import { BreakoutRoomType, PaneContentType, PaneType, PreclassGroupType } from "../types";
import { ReactionType, RoleType, UserType } from "../types/user";

export enum FilterStrategyType {
  LowestFilterCount = "lowest_filter_count",
  VotingYes = "currently_quick_poll_yes_class_room",
  VotingNo = "currently_quick_poll_no_class_room",
  RaisedHand = "currently_hand_raised_class_room",
  RecentlyLiked = "emoted_like_recently",
  RecentlyDisliked = "emoted_dislike_recently",
  RecentlyAgreed = "emoted_agree_recently",
  RecentlyDisagreed = "emoted_disagree_recently",
  RecentlyLaughed = "emoted_laugh_recently",
  RecentlyWowed = "emoted_wow_recently",
  RecentlyLoved = "emoted_love_recently",
  RecentlyClapped = "emoted_clap_recently",
  BreakoutGroup = "breakout_group",
  PreclassGroup = "preclass_group",
}

const VOTE_FILTER_STRATEGY_TO_VOTE_MAP = {
  [FilterStrategyType.VotingYes]: "yes",
  [FilterStrategyType.VotingNo]: "no",
};

export const REACTION_FILTER_STRATEGY_TO_REACTION_MAP: Record<string, ReactionType> = {
  [FilterStrategyType.RecentlyLiked]: "like",
  [FilterStrategyType.RecentlyDisliked]: "dislike",
  [FilterStrategyType.RecentlyAgreed]: "agree",
  [FilterStrategyType.RecentlyDisagreed]: "disagree",
  [FilterStrategyType.RecentlyLaughed]: "laugh",
  [FilterStrategyType.RecentlyWowed]: "wow",
  [FilterStrategyType.RecentlyLoved]: "love",
  [FilterStrategyType.RecentlyClapped]: "clap",
};

// Returns list of users sorted by role: teacher, guest instructor, assistant
function _getSortedStarredUsers(starredUsers: UserType[]) {
  const sortedStarredUsers = [];
  for (const role of [RoleType.Teacher, RoleType.GuestInstructor, RoleType.Assistant]) {
    for (const user of starredUsers) {
      if (user.role === role) {
        sortedStarredUsers.push(user);
      }
    }
  }
  return sortedStarredUsers;
}

function _filterStudentsByStrategy(
  students: UserType[],
  strategy: FilterStrategyType,
  breakoutRoom: BreakoutRoomType | undefined,
  preclassGroup: PreclassGroupType | undefined,
) {
  let studentsFilteredByStrategy: UserType[] = [];
  let remainingStudents: UserType[] = [];

  switch (strategy) {
    case FilterStrategyType.VotingYes:
    case FilterStrategyType.VotingNo: {
      const vote = VOTE_FILTER_STRATEGY_TO_VOTE_MAP[strategy];
      studentsFilteredByStrategy = students.filter((user) => user.vote === vote);
      remainingStudents = students.filter((user) => user.vote !== vote);
      break;
    }
    case FilterStrategyType.RaisedHand:
      studentsFilteredByStrategy = students.filter((user) => user.is_hand_raised);
      remainingStudents = students.filter((user) => !user.is_hand_raised);
      break;
    case FilterStrategyType.RecentlyLiked:
    case FilterStrategyType.RecentlyDisliked:
    case FilterStrategyType.RecentlyAgreed:
    case FilterStrategyType.RecentlyDisagreed:
    case FilterStrategyType.RecentlyLaughed:
    case FilterStrategyType.RecentlyWowed:
    case FilterStrategyType.RecentlyLoved:
    case FilterStrategyType.RecentlyClapped: {
      const reaction = REACTION_FILTER_STRATEGY_TO_REACTION_MAP[strategy];
      studentsFilteredByStrategy = students.filter((user) => user.reaction === reaction);
      remainingStudents = students.filter((user) => user.reaction !== reaction);
      break;
    }
    case FilterStrategyType.BreakoutGroup:
      studentsFilteredByStrategy = students.filter((user) =>
        breakoutRoom?.assigned_user_ids.includes(user.id),
      );
      remainingStudents = students.filter(
        (user) => !breakoutRoom?.assigned_user_ids.includes(user.id),
      );
      break;
    case FilterStrategyType.PreclassGroup:
      studentsFilteredByStrategy = students.filter((user) =>
        preclassGroup?.user_ids.includes(user.id),
      );
      remainingStudents = students.filter((user) => !preclassGroup?.user_ids.includes(user.id));
      break;
    default:
      remainingStudents = students; // lowest_filter_count
  }

  return [studentsFilteredByStrategy, remainingStudents];
}

export function leastTalkTimeSubsort(users: UserType[]) {
  return users.sort((a, b) => (a.classroom_talk_time || 0) - (b.classroom_talk_time || 0));
}

function _leastCumulativeTalkTimeSubsort(users: UserType[]) {
  return users.sort((a, b) => {
    const totalTalkTimeA = a.cumulative_talk_time + (a.classroom_talk_time || 0);
    const totalTalkTimeB = b.cumulative_talk_time + (b.classroom_talk_time || 0);
    return totalTalkTimeA - totalTalkTimeB;
  });
}

// Sort by filter count, then further sort filter count buckets by cumulative talk time
function _leastEngagedSubsort(users: UserType[]) {
  const usersSortedByFilterCount = users.sort(
    (a, b) => (a.filter_count || 0) - (b.filter_count || 0),
  );

  let currentFilterCount = -1;
  let currentUsers: UserType[] = [];
  let sortedResults: UserType[] = [];

  for (const user of usersSortedByFilterCount) {
    if (user.filter_count !== currentFilterCount) {
      sortedResults = sortedResults.concat(_leastCumulativeTalkTimeSubsort(currentUsers));
      currentUsers = [user];
      currentFilterCount = user.filter_count || 0;
    } else {
      currentUsers.push(user);
    }
  }

  sortedResults = sortedResults.concat(_leastCumulativeTalkTimeSubsort(currentUsers));

  return sortedResults;
}

// GOOD comes before POOR
function _connectionQualitySubsort(users: UserType[]) {
  return users.sort((a, b) => {
    if (a.av.connection_quality === b.av.connection_quality) {
      return 0;
    }
    if (a.av.connection_quality === ConnectionQualityLevels.POOR) {
      return 1;
    }
    return -1;
  });
}

// Users without mic issues and who do not have AV preferences open come before users with mic issues
// or users with AV preferences open, then sort each bucket by connection quality
function _mostAbleToDiscussSubsort(users: UserType[]) {
  const prioritizedUsers = users.filter(
    (user) => !user.av.mic_error && !user.is_performing_av_check,
  );
  const deprioritizedUsers = users.filter(
    (user) => !!user.av.mic_error || user.is_performing_av_check,
  );
  return _connectionQualitySubsort(prioritizedUsers).concat(
    _connectionQualitySubsort(deprioritizedUsers),
  );
}

// Partially randomizes previously sorted users list.
// If n equals 8, the first 4 class users will be shuffled with 4 random class users
// from the rest of the list. These 8 will then be recombined with those remaining and
// returned as the reconstructed list.
function _partiallyRandomize(users: UserType[], n: number) {
  const splitIndex = Math.ceil(n / 2);
  let firstHalf = users.slice(0, splitIndex);
  let secondHalf = users.slice(splitIndex);
  let numberOfRandomUsers = n - firstHalf.length;

  if (n === 1) {
    firstHalf = [];
    secondHalf = users;
    numberOfRandomUsers = 1;
  }

  const randomUsers = [];
  for (let i = 0; i < numberOfRandomUsers; i += 1) {
    if (secondHalf.length === 0) {
      break;
    }
    const randomIndex = Math.floor(Math.random() * secondHalf.length);
    const randomUser = secondHalf.splice(randomIndex, 1)[0];
    randomUsers.push(randomUser);
  }

  const shuffledN = shuffle(firstHalf.concat(randomUsers));
  return shuffledN.concat(secondHalf);
}

/*
 * Returns two lists of users, one sorted for the participant bar and one sorted for the stage.
 *
 * For reference:
 * - Starred users = users with the canControlClassroom capability, i.e., teacher + guest instructors + assistants
 * - Students = non-starred users with the canParticipateInClass capability
 * - Other users = non-starred users without the canParticipateInClass capability, i.e., TAs + backup instructors
 *
 * Priority order for participant bar:
 * - Starred users (sorted by role)
 * - Pinned or speaking non-starred users
 * - Students who match the filter strategy (sorted by filter count and cumulative talk time)
 * - Students who do not match the filter strategy (sorted by filter count and cumulative talk time)
 * - Other users
 *
 * Priority order for stage:
 * - Students who match the filter strategy (sorted by filter count and cumulative talk time)
 * - Students who do not match the filter strategy and are not already on stage (sorted by filter count and cumulative talk time)
 * - Students who do not match the filter strategy and are already on stage (sorted by filter count and cumulative talk time)
 * - Starred users (sorted by role)
 * - Other users
 * Note: pinned or speaking non-starred users are excluded from this list as it is assumed they will remain on stage.
 * Note: when filtering for a breakout group, only members of the breakout group should be included in this list.
 *
 * Note: if a filter is provided and no users match the filter, return two empty lists.
 */
export function pickUsers(
  presentVisibleUsers: UserType[],
  isAlwaysSubscribedToUserIds: string[],
  canParticipateInClassUserIds: string[],
  currentPanes: PaneType[],
  strategy: FilterStrategyType = FilterStrategyType.LowestFilterCount,
  participantBarUserIds: string[] = [], // only needed when shuffling/filtering
  randomize: boolean = false, // should be true when shuffling/filtering
  breakoutRoom: BreakoutRoomType | undefined = undefined, // only needed when filtering for a breakout group
  preclassGroup: PreclassGroupType | undefined = undefined, // only needed when filtering for a preclass group
  forTimeline: boolean | undefined = undefined, // only needed when filtering for timeline
) {
  // Filter and sort starred users
  const starredUsers = presentVisibleUsers.filter((user) =>
    isAlwaysSubscribedToUserIds.includes(user.id),
  );
  const sortedStarredUsers = _getSortedStarredUsers(starredUsers);

  // Filter pinned or speaking non-starred users
  const nonStarredUsers = presentVisibleUsers.filter(
    (user) => !isAlwaysSubscribedToUserIds.includes(user.id),
  );
  const pinnedUserIdsOnStage = currentPanes
    .filter((pane) => pane.content_type === PaneContentType.User && pane.is_pinned)
    .map((pane) => pane.content_id);
  const pinnedOrSpeakingNonStarredUsers = nonStarredUsers.filter(
    (user) => pinnedUserIdsOnStage.includes(user.id) || user.is_speaking,
  );

  // Filter students who match the filter strategy and students who do not
  const students = nonStarredUsers.filter(
    (user) =>
      canParticipateInClassUserIds.includes(user.id) &&
      !pinnedOrSpeakingNonStarredUsers.includes(user) &&
      !user.is_speaking,
  );
  let [studentsFilteredByStrategy, remainingStudents] = _filterStudentsByStrategy(
    students,
    strategy,
    breakoutRoom,
    preclassGroup,
  );

  // Filter the remaining other non-starred, non-student users
  const otherUsers = nonStarredUsers.filter(
    (user) =>
      !canParticipateInClassUserIds.includes(user.id) &&
      !pinnedOrSpeakingNonStarredUsers.includes(user) &&
      !user.is_speaking,
  );

  if (
    strategy !== FilterStrategyType.LowestFilterCount &&
    studentsFilteredByStrategy.length === 0 &&
    !forTimeline
  ) {
    return [[], []];
  }

  // Sort and partially randomize the lists
  studentsFilteredByStrategy = _leastEngagedSubsort(studentsFilteredByStrategy);
  remainingStudents = _leastEngagedSubsort(remainingStudents);
  if (randomize) {
    const n =
      participantBarUserIds.length -
      (sortedStarredUsers.length + pinnedOrSpeakingNonStarredUsers.length);
    if (strategy === FilterStrategyType.LowestFilterCount) {
      remainingStudents = _partiallyRandomize(remainingStudents, n);
    } else {
      studentsFilteredByStrategy = _partiallyRandomize(studentsFilteredByStrategy, n);
    }
  }
  studentsFilteredByStrategy = _mostAbleToDiscussSubsort(studentsFilteredByStrategy);

  if (strategy === FilterStrategyType.LowestFilterCount) {
    remainingStudents = _mostAbleToDiscussSubsort(remainingStudents);
  } else {
    // Prioritize students who are already in the participant bar
    const studentsToRemainSubscribedTo = remainingStudents.filter((user) =>
      participantBarUserIds.includes(user.id),
    );
    const otherStudents = remainingStudents.filter(
      (user) => !participantBarUserIds.includes(user.id),
    );
    remainingStudents = [
      ..._mostAbleToDiscussSubsort(studentsToRemainSubscribedTo),
      ..._mostAbleToDiscussSubsort(otherStudents),
    ];
  }

  // Construct list for participant bar
  const sortedUsersForParticipantBar = sortedStarredUsers
    .concat(pinnedOrSpeakingNonStarredUsers)
    .concat(studentsFilteredByStrategy)
    .concat(remainingStudents)
    .concat(_mostAbleToDiscussSubsort(otherUsers));

  // Construct list for stage
  const userIdsOnStage = currentPanes
    .filter((pane) => pane.content_type === PaneContentType.User)
    .map((pane) => pane.content_id);
  const sortedUsersForStage =
    strategy === FilterStrategyType.BreakoutGroup || strategy === FilterStrategyType.PreclassGroup
      ? studentsFilteredByStrategy
      : studentsFilteredByStrategy
          .concat(
            _mostAbleToDiscussSubsort(
              remainingStudents.filter(
                (user) => !userIdsOnStage.includes(user.id) && !user.av.mic_error,
              ),
            ),
          )
          .concat(
            _mostAbleToDiscussSubsort(
              remainingStudents.filter(
                (user) => userIdsOnStage.includes(user.id) || !!user.av.mic_error,
              ),
            ),
          )
          .concat(
            _mostAbleToDiscussSubsort(
              sortedStarredUsers.filter(
                (user) => !pinnedUserIdsOnStage.includes(user.id) && !user.is_speaking,
              ),
            ),
          )
          .concat(_mostAbleToDiscussSubsort(otherUsers));

  return [sortedUsersForParticipantBar, sortedUsersForStage];
}

export function pickUsersForParticipantBar(
  presentVisibleUsers: UserType[],
  isAlwaysSubscribedToUserIds: string[],
  canParticipateInClassUserIds: string[],
  participantBarUserIds: string[],
  currentPanes: PaneType[],
) {
  const pickedUsers = pickUsers(
    presentVisibleUsers,
    isAlwaysSubscribedToUserIds,
    canParticipateInClassUserIds,
    currentPanes,
  )[0];
  // Always pick starred users, then keep as many users who are already in the participant bar as possible,
  // then add whoever else is in the picked users list
  const [partitionA, partitionB] = partition(
    pickedUsers,
    (user) =>
      isAlwaysSubscribedToUserIds.includes(user.id) || participantBarUserIds.includes(user.id),
  );
  return [...partitionA, ...partitionB];
}

export function pickUsersToAddToAndRemoveFromStage(
  presentVisibleUsers: UserType[],
  eligibleUserIds: string[],
  isAlwaysSubscribedToUserIds: string[],
  canParticipateInClassUserIds: string[],
  currentPanes: PaneType[],
) {
  const sortedUsersForStage = pickUsers(
    presentVisibleUsers.filter((user) => eligibleUserIds.includes(user.id)),
    isAlwaysSubscribedToUserIds,
    canParticipateInClassUserIds,
    currentPanes,
  )[1];
  const userIdsOnStage = currentPanes
    .filter((pane) => pane.content_type === PaneContentType.User)
    .map((pane) => pane.content_id);
  return [
    // Exclude users already on stage
    sortedUsersForStage.filter((user) => !userIdsOnStage.includes(user.id)),
    // Only include users already on stage
    sortedUsersForStage.filter((user) => userIdsOnStage.includes(user.id)).reverse(),
  ];
}

/*
 * Participant Replacement Rule
 * Forum should replace non-starred participants with the most talk time
 * Never replaces pinned participants, starred participants, or a currently speaking non-starred participant.
 */
export function pickUserToReplace(
  presentVisibleUsers: UserType[],
  isAlwaysSubscribedToUserIds: string[],
  currentPanes: PaneType[],
  participantBarUserIds: string[] = [],
  onlyReplaceUserOnStage: boolean = false,
) {
  const replaceableUsers = presentVisibleUsers.filter((user) => {
    const isReplaceable =
      participantBarUserIds.includes(user.id) &&
      !isAlwaysSubscribedToUserIds.includes(user.id) &&
      !user.is_speaking &&
      !currentPanes.some(
        (pane) =>
          pane.content_type === PaneContentType.User &&
          pane.content_id === user.id &&
          pane.is_pinned,
      );
    if (onlyReplaceUserOnStage) {
      return (
        isReplaceable &&
        currentPanes.some(
          (pane) => pane.content_type === PaneContentType.User && pane.content_id === user.id,
        )
      );
    }
    return isReplaceable;
  });
  const sortedReplaceableUsers = leastTalkTimeSubsort(replaceableUsers);
  return sortedReplaceableUsers.length > 0
    ? sortedReplaceableUsers[sortedReplaceableUsers.length - 1]
    : null;
}
